import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["title", "message", "delayed", "retry", "followUp", "amount", "transition", "details", "account", "contact", "retryForm"]
  static values = { url: String, state: String, brand: String }

  connect() {
    this.stopped = false
    this.started = Date.now()
    if (this.stateValue === "pending" && !this.transitionShown) this.showTransition()
    else this.showDetails()
    this.transitionShown = true
    this.poll()
  }

  disconnect() {
    this.stopped = true
    clearTimeout(this.timer)
    clearTimeout(this.transitionTimer)
    this.request?.abort()
  }

  showTransition() {
    this.transitioning = true
    this.transitionTarget.hidden = false
    this.detailsTarget.hidden = true
    this.updateDocumentTitle()
    this.transitionTimer = setTimeout(() => this.showDetails(), 2000)
  }

  showDetails() {
    clearTimeout(this.transitionTimer)
    this.transitioning = false
    this.transitionTarget.hidden = true
    this.detailsTarget.hidden = false
    this.updateDocumentTitle()
  }

  updateDocumentTitle() {
    const title = this.transitioning ? this.transitionTarget.textContent.trim() : this.titleTarget.textContent
    document.title = [title, this.brandValue].filter(Boolean).join(" | ")
  }

  async poll() {
    this.request = new AbortController()
    const requestTimeout = setTimeout(() => this.request.abort(), 10000)
    try {
      const url = new URL(this.urlValue, window.location.origin)
      const response = await fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" }, signal: this.request.signal })
      if (response.redirected) {
        window.location.assign(response.url)
        return
      }
      if (response.ok && !this.stopped) {
        const data = await response.json()
        if (this.stopped) return
        if (data.redirect_url) {
          this.stopped = true
          window.location.assign(data.redirect_url)
          return
        }
        this.updateStatus(data)
      }
    } catch (error) {
      if (this.stopped) return
    } finally {
      clearTimeout(requestTimeout)
    }
    if (this.stopped) return
    const delayed = Date.now() - this.started >= 120000
    this.delayedTarget.hidden = this.stateValue !== "pending" || !delayed
    const interval = this.transitioning ? 500 : (delayed || this.stateValue !== "pending" ? 15000 : 3000)
    this.timer = setTimeout(() => this.poll(), interval)
  }

  updateStatus(data) {
    if (!["pending", "failed", "review"].includes(data.state)) return
    if (data.terminal) this.stopped = true

    if (data.state !== this.stateValue) this.started = Date.now()
    this.stateValue = data.state
    if (data.state !== "pending") this.showDetails()
    this.titleTarget.textContent = data.title
    this.updateDocumentTitle()
    this.accountTarget.classList.toggle("btn", data.state === "pending")
    this.accountTarget.classList.toggle("btn-primary", data.state === "pending")
    this.contactTarget.classList.toggle("btn", data.state === "review")
    this.contactTarget.classList.toggle("btn-primary", data.state === "review")
    this.messageTarget.textContent = data.message
    this.followUpTarget.textContent = data.follow_up
    this.amountTarget.textContent = data.amount
    const canRetry = typeof data.retry_url === "string"
    if (this.hasRetryFormTarget) {
      this.retryFormTarget.hidden = typeof data.retry_post_url !== "string"
      if (typeof data.retry_post_url === "string") this.retryFormTarget.action = data.retry_post_url
    }
    this.retryTarget.hidden = !canRetry
    if (data.retry_label) this.retryTarget.textContent = data.retry_label
    if (canRetry) this.retryTarget.href = data.retry_url
    else this.retryTarget.removeAttribute("href")
  }
}
